/**
 * Vista "Olvidé mi contraseña" (sin sesión, desde el login).
 *
 * Paso 1: el promotor toca "Enviarme el código" — el backend manda el código al
 *         correo YA REGISTRADO de su cuenta (resuelto por el device-token de la
 *         caja; acá no se tipea correo alguno — anti-takeover). Se le muestra
 *         enmascarado (m***@gmail.com) para que sepa dónde mirar.
 * Paso 2: código + contraseña nueva (dos veces) → restablecida → al login.
 */

import ApiClient from '../api-client.js';
import { t } from '../languages.js';

const PASSWORD_MIN = 8;

export function renderRecuperarContrasena({ onRestablecida, onVolver }) {
    const el = document.createElement('div');
    el.className = 'login-box';

    let paso = 'solicitar'; // 'solicitar' → 'confirmar'
    let correoMasked = '';
    // Con 2+ cuentas en la caja (turnos): correo que SELECCIONA cuál resetear. El
    // código igual va al correo registrado de esa cuenta (anti-takeover intacto).
    let correoSeleccion = '';
    let pedirSeleccion = false;

    function render() {
        if (paso === 'solicitar') {
            el.innerHTML = `
                <div style="text-align:center; margin-bottom:8px;">
                    <h1 style="margin:0 0 4px;">${t('recuperar.titulo')}</h1>
                    <p style="color:var(--text-dim); margin:0;">${t('recuperar.subtitulo')}</p>
                </div>
                ${pedirSeleccion ? `<input type="email" id="rec-sel-correo" placeholder="${t('activacion.tuCorreo')}" autocomplete="email" inputmode="email" value="${correoSeleccion}">` : ''}
                <div class="error" id="rec-error"></div>
                <button class="btn btn-install" id="rec-enviar">${t('activacion.enviarCodigo')}</button>
                <button class="btn" id="rec-volver" style="margin-top:8px; background:transparent; color:var(--text-dim);">${t('recuperar.volver')}</button>`;
            _bindSolicitar();
        } else {
            el.innerHTML = `
                <div style="text-align:center; margin-bottom:8px;">
                    <h1 style="margin:0 0 4px;">${t('recuperar.titulo')}</h1>
                    <p style="color:var(--text-dim); margin:0;">${t('activacion.codigoEnviado')} <b>${correoMasked}</b></p>
                </div>
                <input type="text" id="rec-codigo" placeholder="${t('activacion.codigo')}" inputmode="numeric" autocomplete="one-time-code" maxlength="6">
                <input type="password" id="rec-pass" placeholder="${t('recuperar.nueva')}" autocomplete="new-password">
                <input type="password" id="rec-pass2" placeholder="${t('activacion.passwordRepetir')}" autocomplete="new-password">
                <div class="error" id="rec-error"></div>
                <button class="btn btn-install" id="rec-restablecer">${t('recuperar.restablecer')}</button>
                <button class="btn" id="rec-reenviar" style="margin-top:8px; background:transparent; color:var(--text-dim);">${t('activacion.reenviar')}</button>`;
            _bindConfirmar();
        }
    }

    function _bindSolicitar() {
        const errEl = el.querySelector('#rec-error');
        const btn = el.querySelector('#rec-enviar');
        el.querySelector('#rec-volver').addEventListener('click', () => onVolver());

        let enProgreso = false;
        btn.addEventListener('click', async () => {
            if (enProgreso) return;
            const selEl = el.querySelector('#rec-sel-correo');
            if (selEl) correoSeleccion = (selEl.value || '').trim().toLowerCase();
            enProgreso = true;
            btn.disabled = true;
            btn.textContent = t('activacion.enviando');
            errEl.textContent = '';
            try {
                const r = await ApiClient.resetSolicitarCodigo(correoSeleccion || undefined);
                if (!r.ok) {
                    // 2+ cuentas en la caja: pedir el correo y reintentar con él.
                    if (r.error === 'multiples_cuentas' && !pedirSeleccion) {
                        pedirSeleccion = true;
                        render();
                        const err = el.querySelector('#rec-error');
                        if (err) err.textContent = r.mensaje || '';
                        return;
                    }
                    errEl.textContent = r.mensaje || t('recuperar.error');
                    return;
                }
                correoMasked = r.correoMasked || '';
                paso = 'confirmar';
                render();
            } finally {
                enProgreso = false;
                if (btn.isConnected) {
                    btn.disabled = false;
                    btn.textContent = t('activacion.enviarCodigo');
                }
            }
        });
    }

    function _bindConfirmar() {
        const codigoEl = el.querySelector('#rec-codigo');
        const passEl = el.querySelector('#rec-pass');
        const pass2El = el.querySelector('#rec-pass2');
        const errEl = el.querySelector('#rec-error');
        const btn = el.querySelector('#rec-restablecer');
        const reenviarBtn = el.querySelector('#rec-reenviar');

        let enProgreso = false;
        async function restablecer() {
            if (enProgreso) return;
            const codigo = (codigoEl.value || '').trim();
            const password = passEl.value || '';
            const passwordRepetida = pass2El.value || '';
            errEl.textContent = '';

            if (!codigo) {
                errEl.textContent = t('activacion.codigoRequerido');
                return;
            }
            if (password.length < PASSWORD_MIN) {
                errEl.textContent = t('activacion.passwordCorta');
                return;
            }
            if (password !== passwordRepetida) {
                errEl.textContent = t('activacion.passwordNoCoincide');
                return;
            }

            enProgreso = true;
            btn.disabled = true;
            btn.textContent = t('recuperar.restableciendo');
            try {
                const r = await ApiClient.resetRestablecerContrasena({
                    codigo,
                    password,
                    passwordRepetida,
                    correo: correoSeleccion || undefined,
                });
                if (!r.ok) {
                    // Código irrecuperable → volver al paso de solicitar uno nuevo.
                    const TERMINALES = ['codigo_consumido', 'EXPIRADO', 'AGOTADO', 'SIN_PENDIENTE'];
                    if (TERMINALES.includes(r.error)) {
                        paso = 'solicitar';
                        render();
                        const err = el.querySelector('#rec-error');
                        if (err) err.textContent = r.mensaje || t('recuperar.error');
                        return;
                    }
                    errEl.textContent = r.mensaje || t('recuperar.error');
                    return;
                }
                onRestablecida();
            } finally {
                enProgreso = false;
                if (btn.isConnected) {
                    btn.disabled = false;
                    btn.textContent = t('recuperar.restablecer');
                }
            }
        }

        btn.addEventListener('click', restablecer);
        pass2El.addEventListener('keydown', e => {
            if (e.key === 'Enter') restablecer();
        });
        reenviarBtn.addEventListener('click', async () => {
            reenviarBtn.disabled = true;
            errEl.textContent = '';
            const r = await ApiClient.resetSolicitarCodigo(correoSeleccion || undefined);
            if (!r.ok) errEl.textContent = r.mensaje || t('recuperar.error');
            setTimeout(() => {
                if (reenviarBtn.isConnected) reenviarBtn.disabled = false;
            }, 5000);
        });
    }

    render();
    return el;
}
